import { useState, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import { ALL_QUESTIONS, computeResult } from "../data/quiz";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { IntroScreen } from "../components/IntroScreen";
import { QuestionScreen } from "../components/QuestionScreen";
import { BlockedScreen } from "../components/BlockedScreen";
import { ResultScreen } from "../components/ResultScreen";

const TOTAL = ALL_QUESTIONS.length;

export default function QuizApp() {
  const [phase, setPhase] = useState("intro");
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const reset = useCallback(() => {
    setAnswers({});
    setQIndex(0);
    setResult(null);
    setPhase("intro");
  }, []);

  const start = useCallback(() => {
    setAnswers({});
    setQIndex(0);
    setPhase("quiz");
  }, []);

  const goBack = useCallback(() => {
    if (qIndex === 0) setPhase("intro");
    else setQIndex((i) => i - 1);
  }, [qIndex]);

  const handleSelect = useCallback(
    (question, option) => {
      setAnswers((prev) => ({ ...prev, [question.id]: option }));

      if (question.section === "safety" && option.id === "yes") {
        setTimeout(() => setPhase("blocked"), 260);
        return;
      }

      setTimeout(() => {
        if (qIndex + 1 >= TOTAL) {
          const next = { ...answers, [question.id]: option };
          setResult(computeResult(next));
          setPhase("result");
        } else {
          setQIndex((i) => i + 1);
        }
      }, 260);
    },
    [qIndex, answers]
  );

  const currentQuestion = ALL_QUESTIONS[qIndex];
  const showBack = phase === "quiz";
  const reading = phase === "intro" || phase === "result";

  return (
    <div
      className={`min-h-screen w-full text-ink antialiased transition-colors duration-500 ${
        reading ? "bg-cream" : "bg-beige"
      }`}
    >
      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-6 pb-6">
        <Header showBack={showBack} onBack={goBack} />

        <main className="flex flex-1 flex-col">
          <AnimatePresence mode="wait">
            {phase === "intro" && <IntroScreen onStart={start} />}

            {phase === "quiz" && (
              <QuestionScreen
                question={currentQuestion}
                index={qIndex}
                total={TOTAL}
                selectedId={answers[currentQuestion.id]?.id}
                onSelect={(opt) => handleSelect(currentQuestion, opt)}
              />
            )}

            {phase === "blocked" && <BlockedScreen onRestart={reset} />}

            {phase === "result" && result && (
              <ResultScreen result={result} onRestart={reset} />
            )}
          </AnimatePresence>
        </main>

        <Footer />
      </div>
    </div>
  );
}
